import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  createOrUpdateSetting,
  getSettingByUser,
  deleteSettingByUuid,
  getSettingByUuid,
} from "./settingsApi";

export const upsertSetting = createAsyncThunk(
  "setting/upsert",
  async ({ data }, thunkAPI) => {
    try {
      const res = await createOrUpdateSetting(data);
      return res?.data ?? res;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data ?? { message: error.message },
      );
    }
  },
);

export const fetchSettingDetailsByUserId = createAsyncThunk(
  "setting/fetchAllSettingForUser",
  async (search = "", thunkAPI) => {
    try {
      const res = await getSettingByUser(search);
      return res?.data ?? res;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data ?? { message: error.message },
      );
    }
  },
);

export const deleteSetting = createAsyncThunk(
  "setting/delete",
  async ({ uuid }, thunkAPI) => {
    try {
      const res = await deleteSettingByUuid(uuid);
      return res;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data ?? { message: error.message },
      );
    }
  },
);

export const fetchSettingById = createAsyncThunk(
  "setting/fetchSettingById",
  async ({ settingUuid }, thunkAPI) => {
    try {
      const res = await getSettingByUuid(settingUuid);
      return res?.data ?? res;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data ?? { message: error.message },
      );
    }
  },
);

const initialState = {
  setting: null,
  settings: [],
  userSettings: [],
  loading: false,
  error: null,
};

const exchangeSlice = createSlice({
  name: "setting",
  initialState,
  reducers: {
    setSetting: (state, action) => {
      state.setting = action.payload;
    },
    clearSetting: (state) => {
      state.setting = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setLoading: (state, action) => {
      state.loading = !!action.payload;
    },
    updateSettingField: (state, action) => {
      if (state.setting) {
        state.setting = { ...state.setting, ...action.payload };
      }
    },
  },

  extraReducers: (builder) => {
    builder

      .addCase(upsertSetting.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(upsertSetting.fulfilled, (state, action) => {
        state.loading = false;
        state.setting = action.payload;
      })
      .addCase(upsertSetting.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchSettingDetailsByUserId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSettingDetailsByUserId.fulfilled, (state, action) => {
        state.loading = false;
        state.userSettings = action.payload;
      })
      .addCase(fetchSettingDetailsByUserId.rejected, (state, action) => {
        state.loading = false;
        state.userSettings = [];
        state.error = action.payload;
      })
      .addCase(deleteSetting.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteSetting.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;

        const deletedUuid = action.meta.arg.uuid;

        state.settings = state.settings.filter((s) => s.uuid !== deletedUuid);
      })
      .addCase(deleteSetting.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchSettingById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSettingById.fulfilled, (state, action) => {
        state.loading = false;
        state.setting = action.payload;
      })
      .addCase(fetchSettingById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setSetting,
  clearSetting,
  clearError,
  setLoading,
  updateSettingField,
} = exchangeSlice.actions;

export default exchangeSlice.reducer;
